<script setup>
import { reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getInboxes'),
  cannedResponses: useMapGetter('cannedResponse/getCannedResponses'),
};

onMounted(async () => {
  // Load canned responses
  console.log('Loading canned responses...');
  try {
    await store.dispatch('cannedResponse/get');
    setTimeout(() => {
      console.log('Canned responses loaded:', formState.cannedResponses.value);
    }, 1000);
  } catch (error) {
    console.error('Failed to load canned responses:', error);
  }
});

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
  templateId: null,
};

const state = reactive({ ...initialState });

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

// Filter inboxes to exclude SMS, WhatsApp, Web Widget and *_vaha inboxes
const filteredInboxes = computed(() => {
  const inboxes = formState.inboxes.value || [];
  return inboxes.filter(inbox => {
    const channelType = inbox.channel_type?.toLowerCase() || '';
    const inboxName = inbox.name?.toLowerCase() || '';

    // Exclude SMS channels (including Twilio)
    if (channelType.includes('sms') || channelType.includes('twilio')) {
      return false;
    }

    // Exclude WhatsApp channels
    if (channelType.includes('whatsapp')) {
      return false;
    }

    // Exclude Web Widget channels
    if (channelType.includes('web') || channelType.includes('widget')) {
      return false;
    }

    // Exclude inboxes with names ending with _vaha or vaha
    if (inboxName.endsWith('_vaha') || inboxName.endsWith('vaha')) {
      return false;
    }

    return true;
  });
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(filteredInboxes.value, 'id', 'name')
);

// Canned response template options
const templateOptions = computed(() => {
  const responses = formState.cannedResponses.value || [];
  const options = responses.map(response => ({
    value: response.id,
    label: response.short_code,
    content: response.content,
  }));
  // Add a "No template" option at the beginning
  return [{ value: null, label: 'No template', content: '' }, ...options];
});

// When template is selected, update the message
const handleTemplateChange = templateId => {
  if (templateId) {
    const responses = formState.cannedResponses.value || [];
    const template = responses.find(r => r.id === templateId);
    if (template) {
      state.message = template.content;
    }
  } else {
    state.message = '';
  }
};

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.CUSTOM.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  scheduled_at: formatToUTCString(state.scheduledAt),
  audience: state.selectedAudience?.map(id => ({
    id,
    type: 'Label',
  })),
  campaign_type: 'one_off', // Custom campaigns are one-off type
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.CUSTOM.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.CUSTOM.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.CUSTOM.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :placeholder="t('CAMPAIGN.CUSTOM.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        @update:model-value="handleTemplateChange"
      />
      <span class="text-xs text-n-slate-10 mt-1">
        {{ t('CAMPAIGN.CUSTOM.CREATE.FORM.TEMPLATE.INFO') }}
      </span>
    </div>

    <TextArea
      v-model="state.message"
      :label="t('CAMPAIGN.CUSTOM.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.CUSTOM.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      show-character-count
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.CUSTOM.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.CUSTOM.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.CUSTOM.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.CUSTOM.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.CUSTOM.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.CUSTOM.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.CUSTOM.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.CUSTOM.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.CUSTOM.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
